import { lazy } from 'react';
import { recoverFromStaleChunk, clearChunkReloadGuard } from './chunkReload';

/**
 * Retry wrapper around React.lazy: if the dynamic import fails (typically a
 * stale chunk hash after a deploy), recover via a service-worker-clearing,
 * cache-busting reload so the user isn't stuck on a broken chunk.
 *
 * Recovery is a ONE-SHOT per stale-chunk event (shared with the global
 * vite:preloadError handler via chunkReload). The guard is cleared on the next
 * successful import so it re-arms for the next deploy — without that, the flag
 * stayed set after the first recovery and the next stale chunk in the tab
 * session threw straight to the error boundary.
 */
export function lazyRetry<T extends { default: any }>(factory: () => Promise<T>) {
  return lazy<T['default']>(() =>
    factory()
      .then((mod) => {
        clearChunkReloadGuard();
        return mod;
      })
      .catch(async (err) => {
        const reloading = await recoverFromStaleChunk();
        if (reloading) return new Promise<T>(() => {}); // page is reloading
        throw err; // already attempted this cycle — surface the error
      })
  );
}
